import { handleApiResponse } from 'utilities/apiUtils';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import '../../styles/sendCertificate.css';
import '../../styles/mastercertificate.css';
import ErrorState from 'components/ErrorState';
import RescheduleDialog from './components/RescheduleDialog';
import CancelScheduleDialog from './components/CancelScheduleDialog';

export type SentCertificate = {
  id: string;
  certificateHolderName: string;
  certificateName: string;
  sentDate: string;
  recipient: string;
  fax: string;
  status: string;
  additionalInterestIndicator: boolean;
  hasCertificateHolder: boolean;
  isSent: boolean;
  sentStatus: string;
  scheduledDateTime: string | null;
};

export type SentCertificatesPanelProps = {
  apiUrl: string;
  token: string;
  insuredDatabaseId: string;
};

function formatDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function formatUsPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return value;
}

const rowsPerPage = 7;

const SentCertificatesPanel: React.FC<SentCertificatesPanelProps> = ({ apiUrl, insuredDatabaseId }) => {
  const [certificates, setCertificates] = React.useState<SentCertificate[]>([]);
  const [page, setPage] = React.useState(0);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [fromDate, setFromDate] = React.useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [toDate, setToDate] = React.useState<Date>(() => new Date());
  const [search, setSearch] = React.useState('');
  const [previewDialog, setPreviewDialog] = React.useState<{ visible: boolean; url: string | null; loading: boolean }>({
    visible: false,
    url: null,
    loading: false,
  });

  const [rescheduleDialog, setRescheduleDialog] = React.useState<{
    visible: boolean;
    certId: string;
    initialDateTime: string | null;
  }>({ visible: false, certId: '', initialDateTime: null });

  const openRescheduleDialog = (cert: SentCertificate) => {
    setRescheduleDialog({ visible: true, certId: cert.id, initialDateTime: cert.scheduledDateTime });
  };

  const closeRescheduleDialog = () => {
    setRescheduleDialog({ visible: false, certId: '', initialDateTime: null });
  };

  const [cancelDialog, setCancelDialog] = React.useState<{ visible: boolean; certId: string; certificateName: string }>({ visible: false, certId: '', certificateName: '' });

  const openCancelDialog = (cert: SentCertificate) => {
    setCancelDialog({ visible: true, certId: cert.id, certificateName: cert.certificateName });
  };

  const closeCancelDialog = () => {
    setCancelDialog({ visible: false, certId: '', certificateName: '' });
  };

  const fetchCertificates = React.useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const skip = pageNum * rowsPerPage;
      let url = `${apiUrl}?top=${rowsPerPage}&skip=${skip}&fromDate=${formatDate(fromDate)}&toDate=${formatDate(toDate)}&insuredDatabaseId=${encodeURIComponent(insuredDatabaseId)}`;
      if (search) url += `&certificateName=${encodeURIComponent(search)}`;
      const res = await fetch(url, {
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      await handleApiResponse(res);
      const data = await res.json();
      setTotalRecords(data['@odata.count'] ?? 0);
      const certs = (data.value || []).map((item: any) => ({
        id: item.databaseId,
        certificateHolderName: item.certificateHolderName || item.name || '-',
        certificateName: item.certificateName || '-',
        sentDate: item.createDate ? new Date(item.createDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-',
        recipient: item.eMail || '',
        fax: item.fax || '',
        status: item.type || '-',
        additionalInterestIndicator: !!item.additionalInterestIndicator,
        hasCertificateHolder: !!(item.certificateHolderName && item.certificateHolderName.trim()),
        isSent: !!item.isSent,
        sentStatus: item.status || '',
        scheduledDateTime: item.scheduledDateTime ?? null,
      }));
      setCertificates(certs);
    } catch (err: any) {
      setError(err.message || 'Error fetching sent certificates');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, insuredDatabaseId, fromDate, toDate, search]);

  // Reset to page 0 and fetch when filters change
  React.useEffect(() => {
    setPage(0);
    fetchCertificates(0);
  }, [fromDate, toDate, search, insuredDatabaseId]);

  const fetchPreviewDownloadUrls = async (certificateId: string) => {
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/download-preview?certificateId=${encodeURIComponent(certificateId)}`;
      const res = await fetch(url, {
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      await handleApiResponse(res);
      const data = await res.json();
      return data;
    } catch (err: any) {
      throw new Error(err.message || 'Error fetching preview/download URLs');
    }
  };

  const handlePreview = async (certificateId: string) => {
    setPreviewDialog({ visible: true, url: null, loading: true });
    try {
      const data = await fetchPreviewDownloadUrls(certificateId);
      if (data.success && data.previewUrl) {
        setPreviewDialog({ visible: true, url: data.previewUrl, loading: false });
      } else {
        throw new Error('Preview URL not available');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading preview');
      setPreviewDialog({ visible: false, url: null, loading: false });
    }
  };

  const handleDownload = async (certificateId: string) => {
    try {
      const data = await fetchPreviewDownloadUrls(certificateId);
      if (data.success && data.downloadUrl) {
        const link = document.createElement('a');
        link.href = data.downloadUrl;
        link.download = `certificate-${certificateId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Download URL not available');
      }
    } catch (err: any) {
      setError(err.message || 'Error downloading certificate');
    }
  };

  return (
    <div className="sentCertPanelOuter" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <div style={{ padding: '10px 14px 8px', flex: '0 0 auto', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          {/* Date From */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label htmlFor="fromDate" style={{ fontSize: 11, fontWeight: 500, color: '#64748b', letterSpacing: '0.01em' }}>Date from</label>
            <Calendar
              id="fromDate"
              value={fromDate}
              onChange={e => setFromDate(e.value as Date)}
              dateFormat="mm/dd/yy"
              placeholder="From"
              showIcon
              style={{ width: 148 }}
              inputStyle={{ height: 32, fontSize: 12, padding: '0 8px' }}
            />
          </div>
          {/* Date To */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label htmlFor="toDate" style={{ fontSize: 11, fontWeight: 500, color: '#64748b', letterSpacing: '0.01em' }}>Date to</label>
            <Calendar
              id="toDate"
              value={toDate}
              onChange={e => setToDate(e.value as Date)}
              dateFormat="mm/dd/yy"
              placeholder="To"
              showIcon
              style={{ width: 148 }}
              inputStyle={{ height: 32, fontSize: 12, padding: '0 8px' }}
            />
          </div>
          {/* Search */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 180 }}>
            <label htmlFor="searchCert" style={{ fontSize: 11, fontWeight: 500, color: '#64748b', letterSpacing: '0.01em' }}>Search</label>
            <span className="p-input-icon-left" style={{ width: '100%' }}>
              <i className="pi pi-search" style={{ fontSize: 11, color: '#94a3b8', left: 9 }} />
              <InputText
                id="searchCert"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search sent certificates..."
                style={{ width: '100%', height: 32, fontSize: 12, paddingLeft: 28, border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: 'none' }}
              />
            </span>
          </div>
          {/* Refresh */}
          <button
            type="button"
            title="Refresh"
            onClick={() => fetchCertificates(page)}
            disabled={loading}
            className="sentCertPanel__refreshBtn"
            style={{ height: 32, width: 32, minWidth: 32 }}
          >
            <i className={`pi pi-refresh${loading ? ' pi-spin' : ''}`} style={{ fontSize: 12 }} />
          </button>
        </div>
      </div>
      {loading ? (
        <div style={{ padding: '0 10px 10px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <table className="sentCertPanel__table" style={{ minWidth: 1000 }}>
            <thead className="sentCertPanel__thead">
              <tr>
                <th style={{ width: '20%' }}>Certificate holder</th>
                <th style={{ width: '24%' }}>Certificate</th>
                {/* <th style={{ width: '9%' }}>Status</th> */}
                <th style={{ width: '10%' }}>Sent</th>
                <th style={{ width: '16%' }}>Email / Fax</th>
                <th style={{ width: '13%' }}>Date</th>
                <th style={{ width: '8%' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="sentCertPanel__tbody">
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><Skeleton width="75%" height={12} /></td>
                  <td><Skeleton width="85%" height={12} /></td>
                  <td><Skeleton width={60} height={22} borderRadius={20} /></td>
                  <td><Skeleton width={50} height={22} borderRadius={20} /></td>
                  <td><Skeleton width="80%" height={12} /></td>
                  <td><Skeleton width="70%" height={12} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Skeleton width={26} height={26} borderRadius={6} />
                      <Skeleton width={26} height={26} borderRadius={6} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : error ? (
        <ErrorState compact />
      ) : certificates.length === 0 ? (
        <div className="panel__emptyState">
          <div className="panel__emptyState__iconWrap">
            <i className="pi pi-send panel__emptyState__icon" />
          </div>
          <div className="panel__emptyState__title">No sent certificates found</div>
          <div className="panel__emptyState__sub">Try adjusting the date range or search, or send your first certificate.</div>
        </div>
      ) : (
        <>
          {/* Set a fixed minHeight for the table container to keep paginator position consistent */}
          <div
            className="sentCertPanel__tableWrap"
            style={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              padding: '0 10px 10px',
            }}
          >
            <table className="sentCertPanel__table" style={{ minWidth: 1000 }}>
              <thead className="sentCertPanel__thead">
                <tr>
                  <th style={{ width: '20%' }}>Certificate holder</th>
                  <th style={{ width: '24%' }}>Certificate</th>
                  {/* <th style={{ width: '9%' }}>Status</th> */}
                  <th style={{ width: '10%' }}>Sent</th>
                  <th style={{ width: '16%' }}>Email / Fax</th>
                  <th style={{ width: '13%' }}>Date</th>
                  <th style={{ width: '8%' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="sentCertPanel__tbody">
                {certificates.map(cert => (
                  <tr key={cert.id}>
                    <td className="sentCert__holderCell">
                      <div className="sentCert__badgeRow">
                        {(cert.hasCertificateHolder || cert.additionalInterestIndicator) && (
                          <span className="sentCert__badges">
                            {cert.hasCertificateHolder && (
                              <span className="sentCert__badge sentCert__badge--holder" title="Certificate holder">H</span>
                            )}
                            {cert.additionalInterestIndicator && (
                              <span className="sentCert__badge sentCert__badge--ai" title="Additional interest">A</span>
                            )}
                          </span>
                        )}
                        <span className="sentCert__holderName" title={cert.certificateHolderName}>
                          {cert.certificateHolderName}
                        </span>
                      </div>
                    </td>
                    <td className="sentCert__certNameCell" title={cert.certificateName}>{cert.certificateName}</td>
                    {/* <td>
                      <span className={
                        cert.status === 'Active'
                          ? 'sentCertPanel__chip sentCertPanel__chip--active'
                          : 'sentCertPanel__chip'
                      }>{cert.status}</span>
                    </td> */}
                    <td>
                      {cert.isSent ? (
                        <span className="sentCertPanel__chip sentCertPanel__chip--sent">Sent</span>
                      ) : cert.scheduledDateTime ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'stretch', borderRadius: 999, overflow: 'hidden', border: '1px solid #c4b5fd', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <span style={{ background: '#A200FF', color: '#fff', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 3 }}>
                              <i className="pi pi-clock" style={{ fontSize: 9 }} />
                              Scheduled
                            </span>
                            <span style={{ background: '#f0ddff', color: '#8500D0', padding: '3px 8px', display: 'flex', alignItems: 'center' }}>
                              {new Date(cert.scheduledDateTime).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                            </span>
                          </span>
                          <i
                            className="pi pi-pencil"
                            title="Edit schedule"
                            onClick={() => openRescheduleDialog(cert)}
                            style={{ fontSize: 11, color: '#A200FF', cursor: 'pointer', padding: 3, borderRadius: 4 }}
                          />
                          <i
                            className="pi pi-times-circle"
                            title="Cancel schedule"
                            onClick={() => openCancelDialog(cert)}
                            style={{ fontSize: 11, color: '#dc2626', cursor: 'pointer', padding: 3, borderRadius: 4 }}
                          />
                        </span>
                      ) : cert.sentStatus === 'Postponed' ? (
                        <span className="sentCertPanel__chip sentCertPanel__chip--canceled">Canceled</span>
                      ) : (
                        <span className="sentCertPanel__chip sentCertPanel__chip--notSent">Queued</span>
                      )}
                    </td>
                    <td className="sentCert__emailCell">
                      {cert.recipient && <div title={cert.recipient}>{cert.recipient}</div>}
                      {cert.fax && <div title={cert.fax}>{formatUsPhone(cert.fax)}</div>}
                      {!cert.recipient && !cert.fax && '-'}
                    </td>
                    <td className="sentCert__dateCell">{cert.sentDate}</td>
                    <td>
                      <div className="sentCert__actions">
                        <i
                          className="pi pi-eye sentCert__actionIcon sentCert__actionIcon--preview"
                          onClick={() => handlePreview(cert.id)}
                          title="Preview certificate"
                        />
                        <i
                          className="pi pi-download sentCert__actionIcon sentCert__actionIcon--download"
                          onClick={() => handleDownload(cert.id)}
                          title="Download certificate"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {Array.from({ length: rowsPerPage - certificates.length }).map((_, idx) => (
                  <tr key={"empty-" + idx}>
                    <td colSpan={7} style={{ border: 'none', background: 'transparent' }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ flex: '0 0 auto', padding: '8px 12px', borderTop: '1px solid #e6e7ef', background: '#fff', display: 'flex', justifyContent: 'center' }}>
            <Paginator
              rows={rowsPerPage}
              totalRecords={totalRecords}
              first={page * rowsPerPage}
              onPageChange={e => {
                const newPage = Math.floor(e.first / rowsPerPage);
                setPage(newPage);
                fetchCertificates(newPage);
              }}
              template="PrevPageLink PageLinks NextPageLink"
              className="requests-paginator p-paginator p-component"
            />
          </div>
        </>
      )}

      <CancelScheduleDialog
        visible={cancelDialog.visible}
        certId={cancelDialog.certId}
        certificateName={cancelDialog.certificateName}
        insuredDatabaseId={insuredDatabaseId}
        onHide={closeCancelDialog}
        onSuccess={() => { closeCancelDialog(); fetchCertificates(page); }}
      />

      <RescheduleDialog
        visible={rescheduleDialog.visible}
        certId={rescheduleDialog.certId}
        initialDateTime={rescheduleDialog.initialDateTime}
        insuredDatabaseId={insuredDatabaseId}
        onHide={closeRescheduleDialog}
        onSuccess={() => { closeRescheduleDialog(); fetchCertificates(page); }}
      />

      <Dialog
        header="Certificate Preview"
        visible={previewDialog.visible}
        style={{ width: '50vw', height: '90vh' }}
        onHide={() => setPreviewDialog({ visible: false, url: null, loading: false })}
        maximizable
      >
        {previewDialog.loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <ProgressSpinner />
          </div>
        ) : previewDialog.url ? (
          <iframe
            src={previewDialog.url}
            style={{ width: '100%', height: '75vh', border: 'none' }}
            title="Certificate preview"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Unable to load preview</p>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default SentCertificatesPanel;
